import { Number, Optional, Record, Static, String } from "runtypes";

import {
  AppSessionId,
  CellType,
  GoogleDriveOauthAccountId,
  HexId,
  OrgId,
  RunNotificationRunStatus,
  RunNotificationTrigger,
  ScheduleNotificationConditionId,
  ScheduleNotificationConditionPredicate,
  ScheduleNotificationConditionType,
  ScheduleNotificationContentType,
  ScheduleNotificationExportId,
  ScheduledRunNotificationType,
  ScreenshotFormat,
  ScreenshotTarget,
  SlackChannelId,
  StaticCellId,
  StaticGridLayoutId,
  UserId,
  assertNever,
} from "./index.js";

export const MAX_NOTIFICATION_ATTACHMENT_COUNT = 10;
// AWS SES has a default limit of 10mb emails. We can request higher limits if we find we need to send larger emails
export const MAX_TOTAL_NOTIFICATION_ATTACHMENTS_SIZE_BYTES = 9_000_000; // 9mb buffer to account for message contents and other overhead
export const MAX_TOTAL_NOTIFICATION_ATTACHMENTS_SIZE_BYTES_SLACK = 50_000_000; // 50mb - this is chosen somewhat arbitrarily; slack can handle more (up to 1gb per file) but we probably don't want to send that much for notifications. We can adjust this upwards if customers run into this

export function getConditionDescriptor(
  cellTitle: string,
  conditionType: ScheduleNotificationConditionType,
  value: string,
): string {
  switch (conditionType) {
    case ScheduleNotificationConditionType.METRIC_CELL:
      return `${cellTitle} is ${value}`;
    case ScheduleNotificationConditionType.TABLE_ROW_COUNT:
      return `${cellTitle} has row count of ${value}`;
    default:
      assertNever(conditionType, conditionType);
  }
}

export type ValidNotificationExportTypes = "CSV" | "GOOGLE_SHEETS";

// ids are optional because they will be created at the time of export if the user is creating a new sheet
export const GoogleSheetsConfig = Record({
  folder: Optional(
    Record({
      id: String,
      name: String,
    }),
  ),
  spreadsheetId: Optional(String),
  spreadsheetName: String,
  tabId: Optional(Number),
  tabName: String,
});
export type GoogleSheetsConfig = Static<typeof GoogleSheetsConfig>;

/**
 * Generates a Google Sheets URL from a spreadsheet ID and optional tab ID
 * @returns The full Google Sheets URL, or null if spreadsheetId is not provided
 */
export function getGoogleSheetsUrl(
  spreadsheetId: string | undefined,
  tabId?: number | undefined,
): string | null {
  if (!spreadsheetId) {
    return null;
  }
  return tabId != null
    ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${tabId}`
    : `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
}

export const VALID_NOTIFICATION_ATTACHMENT_CELL_TYPES: CellType[] = [
  CellType.DISPLAY_TABLE,
  CellType.SQL,
  // Pivot visualizations are the only supported viz type for explores
  CellType.EXPLORE,
];

export const notificationPredicateToHuman: {
  [T in ScheduleNotificationConditionPredicate]: string;
} = {
  [ScheduleNotificationConditionPredicate.EQ]: "is equal to",
  [ScheduleNotificationConditionPredicate.NEQ]: "is not equal to",
  [ScheduleNotificationConditionPredicate.CONTAINS]: "contains",
  [ScheduleNotificationConditionPredicate.NOT_CONTAINS]: "does not contain",
  [ScheduleNotificationConditionPredicate.GT]: "is greater than",
  [ScheduleNotificationConditionPredicate.GTE]: "is greater than or equal to",
  [ScheduleNotificationConditionPredicate.LT]: "is less than",
  [ScheduleNotificationConditionPredicate.LTE]: "is less than or equal to",
};

export interface EvaluatedCondition {
  id: ScheduleNotificationConditionId;
  staticCellId?: StaticCellId;
  // A full string representation of the condition was met and the
  // value that was compared against it, suitable for notification body
  // e.g. "Revenue is 100, which is greater than 50"
  description: string;
  // A string representation of the condition that was met,
  // suitable for notification subject/title (nullable for backwards compatibility)
  // e.g. "Revenue is 100"
  conditionValueString?: string;
  result?: boolean;
  userFacingError?: string;
}

export interface NotificationExport {
  id: ScheduleNotificationExportId;
  fileName?: string;
  googleDriveOauthAccountId?: GoogleDriveOauthAccountId;
  googleSheetsConfig?: GoogleSheetsConfig;
  format: ValidNotificationExportTypes;
  staticCellId: StaticCellId;
}

export interface GoogleSheetsExportResult {
  url: string;
  displayName: string;
}

export interface ExportError {
  fileName: string;
  error?: string;
}

export interface RunNotification {
  userId: UserId | null;
  slackChannelId: SlackChannelId | null;
  messageConfig: { subject: string | null; body: string | null };
  evaluatedConditions: EvaluatedCondition[];
  exports: NotificationExport[];
  screenshotFormats: ScreenshotFormat[];
  screenshotTarget?: ScreenshotTarget;
  staticGridLayoutIds?: StaticGridLayoutId[] | null;
  chartStaticCellIds?: StaticCellId[] | null;
  trigger: RunNotificationTrigger;
  runStatus: RunNotificationRunStatus;
  scheduledRunNotificationType?: ScheduledRunNotificationType;
  contentType?: ScheduleNotificationContentType;
  userRequestedScreenshot: boolean;
  googleSheetsExports?: GoogleSheetsExportResult[];
  googleSheetsExportErrors?: ExportError[];
}

const TEST_INDICATOR = "[Test Run]";

export const getSimplifiedNotificationTitle = ({
  isEmbeddedApp = false,
  projectName,
  runStatus,
  suffix = "",
}: {
  isEmbeddedApp?: boolean;
  projectName: string;
  runStatus: RunNotificationRunStatus;
  suffix?: string;
}): string => {
  switch (runStatus) {
    case RunNotificationRunStatus.FAILED:
      return `❌ ${projectName}${suffix} has failed to run.`;
    case RunNotificationRunStatus.SUCCEEDED:
      return isEmbeddedApp
        ? `✅ ${projectName}${suffix} is now available.`
        : `✅ ${projectName}${suffix} has finished running.`;
    case RunNotificationRunStatus.TIMED_OUT:
      return `⏱️ ${projectName}${suffix} has timed out.`;
    default:
      assertNever(runStatus, runStatus);
  }
};

export const composeSimpleRunNotification = ({
  escapeFormatter,
  hasConditions = false,
  hexTitle,
  hexTitleFormatter = (title) => title,
  isEmbeddedApp = false,
  runNotification,
  savedViewName,
  skipTestHeader = false,
}: {
  runNotification: RunNotification;
  hexTitleFormatter?: (title: string) => string;
  escapeFormatter: (rawString: string) => string;
  skipTestHeader?: boolean;
  hexTitle: string;
  hasConditions?: boolean;
  savedViewName: string | null;
  isEmbeddedApp?: boolean;
}): string => {
  const savedViewSuffix = savedViewName
    ? ` on the ${hexTitleFormatter(escapeFormatter(savedViewName))} view`
    : "";
  const formattedTitle = hexTitleFormatter(escapeFormatter(hexTitle));

  if (hasConditions) {
    const scheduledRunDescription = isEmbeddedApp
      ? "Scheduled data delivery"
      : "A scheduled run";
    let triggerDescription: string = "A run";
    switch (runNotification.trigger) {
      case RunNotificationTrigger.API: {
        triggerDescription = "An API triggered run";
        break;
      }
      case RunNotificationTrigger.BACKGROUND_DATA_REFRESH: {
        triggerDescription = "A background data refresh run";
        break;
      }
      case RunNotificationTrigger.SCHEDULE: {
        triggerDescription = scheduledRunDescription;
        break;
      }
      case RunNotificationTrigger.TEST_SCHEDULE: {
        // We may skipTestHeader when this is used in a structured message
        triggerDescription = skipTestHeader
          ? scheduledRunDescription
          : `${TEST_INDICATOR} ${scheduledRunDescription}`;
        break;
      }
      default:
        assertNever(runNotification.trigger, runNotification.trigger);
    }
    triggerDescription = escapeFormatter(triggerDescription);

    const projectTitleText = isEmbeddedApp ? "" : ` of ${formattedTitle}`;
    return `This notification was triggered by ${triggerDescription.toLowerCase()}${projectTitleText}${savedViewSuffix}.`;
  }

  // Use simplified format: "✅ ProjectName has finished running."
  return getSimplifiedNotificationTitle({
    projectName: formattedTitle,
    runStatus: runNotification.runStatus,
    isEmbeddedApp,
    suffix: savedViewSuffix,
  });
};

export const composeStructuredRunNotification = ({
  escapeFormatter = (rawString: string) => rawString,
  exportErrors = [],
  hexTitle,
  hexTitleFormatter,
  isEmbeddedApp = false,
  runNotification: rn,
  savedViewName,
}: {
  exportErrors?: { error?: string; fileName: string }[];
  runNotification: RunNotification;
  hexTitleFormatter?: (title: string) => string;
  escapeFormatter?: (rawString: string) => string;
  hexTitle: string;
  isEmbeddedApp?: boolean;
  savedViewName: string | null;
}): {
  title: string;
  contents: string[];
  structuredContents: {
    body: string | null;
    runDescription: string;
    conditions: string[];
    errors: string[];
  };
} => {
  const simpleRunNotificationArgs = {
    hexTitle,
    escapeFormatter,
    hexTitleFormatter,
    isEmbeddedApp,
    runNotification: rn,
    savedViewName,
  };

  const failedRunSubscribedToAll =
    rn.scheduledRunNotificationType === ScheduledRunNotificationType.ALL &&
    rn.runStatus !== RunNotificationRunStatus.SUCCEEDED;

  // If there is no message config and no conditions or this is a failed run subscribed to "all",
  // we just return a simple message
  if (
    (!rn.messageConfig.subject &&
      !rn.messageConfig.body &&
      rn.evaluatedConditions.length === 0 &&
      exportErrors.length === 0) ||
    failedRunSubscribedToAll
  ) {
    const simple = composeSimpleRunNotification(simpleRunNotificationArgs);
    return {
      title: simple,
      contents: [],
      structuredContents: {
        errors: [],
        runDescription: simple,
        conditions: [],
        body: null,
      },
    };
  }

  const runDescription = composeSimpleRunNotification({
    ...simpleRunNotificationArgs,
    skipTestHeader: true,
    hasConditions: rn.evaluatedConditions.length > 0,
  });

  let title = runDescription;
  let contents = [];
  let body = null;

  if (rn.messageConfig.body) {
    body = escapeFormatter(rn.messageConfig.body);
    contents.push(body);
  }

  // For stability, sort conditions by ID, and then have errors come last
  const evaluatedConditions = rn.evaluatedConditions
    .sort((a, b) => a.id.localeCompare(b.id))
    .sort((a, b) => {
      if (a.userFacingError && !b.userFacingError) return 1;
      if (!a.userFacingError && b.userFacingError) return -1;
      return 0;
    });

  const conditions: string[] = [];
  const conditionErrors: string[] = [];
  for (const cond of evaluatedConditions) {
    if (cond.userFacingError) {
      const errorMessage = `There was an error in evaluating "${escapeFormatter(cond.description)}": ${cond.userFacingError}.`;
      conditions.push(errorMessage);
      conditionErrors.push(errorMessage);
    } else {
      conditions.push(`${escapeFormatter(cond.description)}.`);
    }
  }

  // If there were conditions, we build the contents slightly differently
  if (evaluatedConditions.length > 0) {
    // Derive the title of the notification from the first condition
    // that was evaluated successfully
    const firstCondition =
      rn.trigger === RunNotificationTrigger.TEST_SCHEDULE
        ? evaluatedConditions[0]
        : evaluatedConditions.find((c) => c.result);
    if (firstCondition != null) {
      title = `${escapeFormatter(firstCondition?.conditionValueString ?? firstCondition?.description)}`;
    }

    // Keep runDescription for conditional notifications
    contents = [runDescription];

    // Always start with the user-provided body, even in the presence of conditions
    if (body != null) {
      contents.unshift(body!);
    }
  }

  // If there was a custom subject, always use that as the title
  // (regardless of whether there were conditions)
  if (rn.messageConfig.subject) {
    title = escapeFormatter(rn.messageConfig.subject);
  }

  if (rn.trigger === RunNotificationTrigger.TEST_SCHEDULE) {
    title = `${TEST_INDICATOR} ${title}`;
  }

  const errorMessages =
    exportErrors.length > 0
      ? [
          `There were errors in generating the following exports: ${exportErrors.map((e) => `${e.fileName} ${e.error ? `(${e.error})` : ""}`).join(", ")}`,
        ]
      : [];

  // Add condition errors to contents so users can see why condition evaluation failed
  contents = [...contents, ...conditionErrors, ...errorMessages];

  return {
    title,
    contents,
    structuredContents: {
      body,
      runDescription,
      conditions,
      errors: errorMessages,
    },
  };
};

export const generateSuccessNotificationProjectLink = ({
  appSessionId,
  baseUrl,
  hexId,
  isTestNotification,
  orgId,
}: {
  baseUrl: string;
  orgId: OrgId;
  hexId: HexId;
  appSessionId: AppSessionId;
  isTestNotification: boolean;
}): string => {
  // Test notifications send off of the user's current app session, and it may not be collaborative.
  // Instead, always link to /latest.
  if (isTestNotification) {
    return `${baseUrl}/${orgId}/app/${hexId}/latest`;
  }
  return `${baseUrl}/${orgId}/app/${hexId}/app/${appSessionId}`;
};
